import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import './PrivacyPolicy.css';

export function PrivacyPolicy() {
  return (
    <div className="privacy-policy">
      <header className="privacy-policy__header">
        <div className="privacy-policy__header-brand"></div>
        <Link to="/dashboard" className="privacy-policy__back-btn">
          <ArrowLeft size={18} />
          <span>Back to Dashboard</span>
        </Link>
      </header>

      <main className="privacy-policy__container">
        <article className="privacy-policy__card">
          <h1 className="privacy-policy__title">Privacy Policy</h1>
          <p className="privacy-policy__last-updated">Last Updated: June 10, 2026</p>

          <section className="privacy-policy__intro">
            <p>
              This Privacy Policy describes how LTRJ Labs LTD. ("we," "us," or "our") collects, uses, discloses, and protects your personal information when you use our platform, MothNode Dashboard (the "Platform").
            </p>
            <p>
              Because we operate in Canada and the United States, this policy is structured to comply with the Personal Information Protection and Electronic Documents Act (PIPEDA), provincial regulations like the BC Personal Information Protection Act (PIPA), and relevant US state-level privacy frameworks.
            </p>
          </section>

          <hr className="privacy-policy__divider" />

          <section className="privacy-policy__section">
            <h2>1. Information We Collect</h2>
            <p>
              We limit the collection of personal information strictly to what is necessary to operate the Platform and deliver your requested services.
            </p>
            <ul className="privacy-policy__list">
              <li>
                <strong>Account Information:</strong> When you register, we collect your First Name, Last Name, Email Address, and Phone Number. Passwords are securely managed directly via AWS Cognito; we never view or store your plain-text password.
              </li>
              <li>
                <strong>Ground Measurement Data (Telemetry):</strong> The Platform processes environmental, mechanical, or physical telemetry sent via API from your monitoring equipment to designated folders.
              </li>
            </ul>
          </section>

          <section className="privacy-policy__section">
            <h2>2. How We Use Your Information</h2>
            <p>
              We use your data strictly for the following purposes:
            </p>
            <ul className="privacy-policy__list">
              <li>
                <strong>Account Provisioning:</strong> Managing your secure access to the platform via AWS Cognito.
              </li>
              <li>
                <strong>Email Communications:</strong> Sending system updates, password resets, and user-configured threshold alerts using AWS SES.
              </li>
              <li>
                <strong>SMS Notifications:</strong> Delivering automated threshold alerts directly to your device via Twilio, strictly based on your explicit dashboard configurations.
              </li>
              <li>
                <strong>Access Delegation:</strong> Generating unique API keys that allow you or your designated devices to push ground telemetry to your folders.
              </li>
            </ul>
          </section>

          <section className="privacy-policy__section">
            <h2>3. Data Sharing and Administrator Visibility</h2>
            <p>
              A core function of MothNode Dashboard is the collaborative viewing of ground data.
            </p>
            <ul className="privacy-policy__list">
              <li>
                <strong>Area Administrators:</strong> If you join an area or a group folder, your First Name, Last Name, and Email Address will be visible to designated Administrators of that specific area to ensure system transparency and audit capability.
              </li>
              <li>
                <strong>External Invitations:</strong> If a user inputs an external email address to invite a non-account holder to view data, we process that email address in volatile memory strictly to transmit the invitation via AWS SES. We do not store or retain unverified external emails in our permanent databases.
              </li>
            </ul>
          </section>

          <section className="privacy-policy__section">
            <h2>4. Third-Party Service Providers</h2>
            <p>
              We do not sell, rent, or trade your personal information. We utilize trusted infrastructure partners to host and run our services, who are legally bound to protect your data:
            </p>
            <ul className="privacy-policy__list">
              <li>
                <strong>Amazon Web Services (AWS):</strong> Used for account authentication (Cognito), transactional database management (DynamoDB), and automated emailing (SES).
              </li>
              <li>
                <strong>Twilio:</strong> Used exclusively to process and route user-defined SMS alerts.
              </li>
            </ul>
          </section>

          <section className="privacy-policy__section">
            <h2>5. Data Retention and Deletion</h2>
            <ul className="privacy-policy__list">
              <li>
                <strong>Account PII:</strong> If you choose to close your account, your personal information (Name, Email, Phone Number, and Cognito profile) is deleted immediately from our active production systems.
              </li>
              <li>
                <strong>Ground Telemetry:</strong> Because historical ground measurement data is completely decoupled from your personal identity once an account is deleted, it is retained indefinitely in our storage folders for ongoing data integrity and grouping metrics.
              </li>
            </ul>
          </section>

          <section className="privacy-policy__section">
            <h2>6. Communications Opt-Out</h2>
            <ul className="privacy-policy__list">
              <li>
                <strong>SMS:</strong> You can deactivate text notifications at any time through your web dashboard or by replying STOP to any text message received.
              </li>
              <li>
                <strong>Email:</strong> You can manage or disable specific threshold email triggers directly inside your account settings.
              </li>
            </ul>
          </section>
        </article>
      </main>
    </div>
  );
}

export default PrivacyPolicy;
